import '@pancakeswap/ui/css/reset.css'
import { ResetCSS, ScrollToTopButtonV2, ToastListener } from '@pancakeswap/uikit'
import BigNumber from 'bignumber.js'
import GlobalCheckClaimStatus from 'components/GlobalCheckClaimStatus'
import { NetworkModal } from 'components/NetworkModal'
import { FixedSubgraphHealthIndicator } from 'components/SubgraphHealthIndicator/FixedSubgraphHealthIndicator'
import TransactionsDetailModal from 'components/TransactionDetailModal'
import { useAccountEventListener } from 'hooks/useAccountEventListener'
import useEagerConnect from 'hooks/useEagerConnect'
import useEagerConnectMP from 'hooks/useEagerConnect.bmp'
import useLockedEndNotification from 'hooks/useLockedEndNotification'
import useSentryUser from 'hooks/useSentryUser'
import useThemeCookie from 'hooks/useThemeCookie'
import useUserAgent from 'hooks/useUserAgent'
import { NextPage } from 'next'
import type { AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import Script from 'next/script'
import { Fragment } from 'react'
import { DefaultSeo } from 'next-seo'
import { PageMeta } from 'components/Layout/Page'
import { PersistGate } from 'redux-persist/integration/react'
import { persistor, useStore } from 'state'
import { usePollBlockNumber } from 'state/block/hooks'
import styled from 'styled-components'
import { AtomBox } from '@pancakeswap/ui/components/AtomBox'
import { Updaters } from '..'
import { SEO } from '../../next-seo.config'
import { SentryErrorBoundary } from '../components/ErrorBoundary'
import Menu from '../components/Menu'
import LeftMenu from '../components/MenuForEkey/LeftMenu'
import BottomMenu from '../components/MenuForEkey/BottomMenu'
import HeaderBar from '../components/HeaderBar'
import Footer from '../components/Footer'
import Providers from '../Providers'
import GlobalStyle from '../style/Global'

const EasterEgg = dynamic(() => import('components/EasterEgg'), { ssr: false })

// This config is required for number formatting
BigNumber.config({
  EXPONENTIAL_AT: 1000,
  DECIMAL_PLACES: 80,
})

function GlobalHooks() {
  usePollBlockNumber()
  useEagerConnect()
  useUserAgent()
  useAccountEventListener()
  useSentryUser()
  useThemeCookie()
  useLockedEndNotification()
  return null
}

function MPGlobalHooks() {
  usePollBlockNumber()
  useEagerConnectMP()
  useUserAgent()
  useAccountEventListener()
  useSentryUser()
  useLockedEndNotification()
  return null
}

function MyApp(props: AppProps<{ initialReduxState: any }>) {
  const { pageProps, Component } = props
  const store = useStore(pageProps.initialReduxState)

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=5, minimum-scale=1, viewport-fit=cover"
        />
        <meta name="description" content="The simplest, most reliable, highest-yielding Web3 infrastructure." />
        <meta name="theme-color" content="#7357FF" />
        {(Component as NextPageWithLayout).mp && (
          // eslint-disable-next-line @next/next/no-sync-scripts
          <script src="https://public.bnbstatic.com/static/js/mp-webview-sdk/webview-v1.0.0.min.js" id="mp-webview" />
        )}
      </Head>
      <DefaultSeo {...SEO} />
      <Providers store={store}>
        <PageMeta />
        {(Component as NextPageWithLayout).Meta && (
          // @ts-ignore
          <Component.Meta {...pageProps} />
        )}
        {(Component as NextPageWithLayout).mp ? <MPGlobalHooks /> : <GlobalHooks />}
        <ResetCSS />
        <GlobalStyle />
        <GlobalCheckClaimStatus excludeLocations={[]} />
        <PersistGate loading={null} persistor={persistor}>
          <Updaters />
          <App {...props} />
        </PersistGate>
      </Providers>
    </>
  )
}

type NextPageWithLayout = NextPage & {
  Layout?: React.FC<React.PropsWithChildren<unknown>>
  /** render component without all layouts */
  pure?: true
  /** is mini program */
  mp?: boolean
  /**
   * allow chain per page, empty array bypass chain block modal
   * @default [ChainId.BSC]
   * */
  chains?: number[]
  isShowScrollToTopButton?: true
  /**
   * Meta component for page, hacky solution for static build page to avoid `PersistGate` which blocks the page from rendering
   */
  Meta?: React.FC<React.PropsWithChildren<unknown>>
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

const ProductionErrorBoundary = process.env.NODE_ENV === 'production' ? SentryErrorBoundary : Fragment

const LayoutContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
`
const LayoutLeft = styled.div`
  background-color: #2e293d;
  width: 218px;
  position: relative;
  z-index: 98;
`
const LayoutRight = styled.div`
  background-color: #110c22;
  width: 100%;
  overflow: auto;
  position: relative;
  ${({ theme }) => theme.mediaQueries.md} {
    width: calc(100% - 218px);
  }
`
const LayoutRightHeader = styled.div`
  width: 100%;
  top: 0;
  right: 0;
  height: 90px;
  position: fixed;
  z-index: 97;
  background-color: #110c22;
`
const LayoutRightFooter = styled.div`
  position: fixed;
  bottom: 0;
  right: 0;
  width: 100%;
  z-index: 98;
`
const LayoutRightContent = styled.div`
  padding-top: 90px;
  padding-bottom: 88px;
  ${({ theme }) => theme.mediaQueries.md} {
    padding-bottom: 0px;
  }
`

const App = ({ Component, pageProps }: AppPropsWithLayout) => {
  if (Component.pure) {
    return <Component {...pageProps} />
  }
  // Use the layout defined at the page level, if available
  const Layout = Component.Layout || Fragment
  const ShowMenu = Component.mp ? Fragment : Menu
  const isShowScrollToTopButton = Component.isShowScrollToTopButton || true
  return (
    // <ProductionErrorBoundary>
    <>
      <LayoutContainer>
        <AtomBox asChild display={{ xs: 'none', md: 'block' }}>
          <LayoutLeft>
            <LeftMenu />
          </LayoutLeft>
        </AtomBox>
        <LayoutRight>
          <LayoutRightHeader>
            <HeaderBar />
          </LayoutRightHeader>
          <LayoutRightContent>
            <Layout>
              <Component {...pageProps} />
            </Layout>
            <Footer />
          </LayoutRightContent>
          <AtomBox display={{ xs: 'block', md: 'none' }}>
            <LayoutRightFooter>
              <BottomMenu />
            </LayoutRightFooter>
          </AtomBox>
        </LayoutRight>
      </LayoutContainer>
      {/* <ShowMenu>
        <Layout>
          <Component {...pageProps} />
        </Layout>
      </ShowMenu> */}
      <EasterEgg iterations={2} />
      <ToastListener />
      <FixedSubgraphHealthIndicator />
      <NetworkModal pageSupportedChains={Component.chains} />
      <TransactionsDetailModal />
      {isShowScrollToTopButton && <ScrollToTopButtonV2 />}
    </>
    // </ProductionErrorBoundary>
  )
}

export default MyApp
